/**
 * Invoicing admin API — `/api/v2/invoicing/*`. All endpoints are
 * admin-only (Bearer + role admin, handled by the shared instance).
 * See ./invoicingProducts.ts and ./invoicingBillingAddresses.ts for the
 * catalog + billing-address sub-resources.
 */
import instance, { ADMIN_PREFIX } from 'services/api';
import {
  BankReceiptInput,
  CountryOption,
  CreateInvoiceInput,
  CreateInvoiceResponse,
  CreditNoteInput,
  Invoice,
  InvoiceDetail,
  InvoiceListFilters,
  InvoiceListResponse,
  MarkPaidInput,
  NumberCounter,
  PartyResult
} from 'types/api/invoicing';

const BASE = `${ADMIN_PREFIX}/invoicing`;

/**
 * `GET /invoicing/countries?lang=` — ISO-3166 alpha-2 list, localized + sorted
 * by name. The authoritative source for the country selects (matches the BE's
 * country validation).
 */
export const fetchCountries = async (
  lang: 'en' | 'de' = 'en'
): Promise<CountryOption[]> => {
  const res = await instance.get<CountryOption[]>(`${BASE}/countries`, {
    params: { lang }
  });

  return res.data ?? [];
};

/**
 * `GET /invoicing/:ref/creditable` — how much of this invoice can still be
 * credited, in major units. Several partial credit notes are allowed, so this
 * shrinks with each one; use it to bound the amount input.
 */
export const fetchCreditable = async (publicId: string): Promise<number> => {
  const res = await instance.get<{ remaining: number }>(
    `${BASE}/${publicId}/creditable`
  );

  return Number(res.data?.remaining ?? 0);
};

/**
 * `POST /invoicing/:ref/credit-notes` — issue a credit note against this
 * invoice. Returns the note: a document with a NEGATIVE total, numbered from
 * the same sequence as invoices.
 *
 * Refused by the BE on a draft, on a cancelled invoice, on a credit note
 * itself, over the remaining amount, and for a Stripe refund where there is
 * no Stripe payment — each with its own message, so surface the error.
 */
export const createCreditNote = async (
  publicId: string,
  input: CreditNoteInput
): Promise<Invoice> => {
  const res = await instance.post<Invoice>(
    `${BASE}/${publicId}/credit-notes`,
    input
  );

  return res.data;
};

/**
 * `GET /invoicing/parties?search=&limit=` — one search across BOTH party
 * tables (platform patients and billing-only customers).
 *
 * Replaces `GET /patients?search=` for the invoice party picker only; that
 * endpoint is still correct everywhere else, it simply cannot see billing
 * customers. An empty search returns [] — this is a picker, not a listing.
 *
 * `search` also accepts a birthday (DD.MM.YYYY or YYYY-MM-DD), on its own or
 * combined with a name. Deleted patients are excluded by the BE.
 */
export const fetchParties = async (
  search: string,
  limit = 20
): Promise<PartyResult[]> => {
  if (!search.trim()) {
    return [];
  }

  const res = await instance.get<PartyResult[]>(`${BASE}/parties`, {
    params: { search, limit },
    // An admin invoices any party regardless of the org picked in the header.
    headers: { organization: null as unknown as string }
  });

  return res.data ?? [];
};

/** `GET /invoicing` — filtered, paginated list. Envelope: {data,total,page,limit}. */
export const fetchInvoices = async (
  filters: InvoiceListFilters = {}
): Promise<InvoiceListResponse> => {
  const res = await instance.get<InvoiceListResponse>(BASE, {
    params: filters
  });

  return {
    data: res.data.data ?? [],
    total: res.data.total ?? 0,
    page: res.data.page ?? 1,
    limit: res.data.limit ?? 0
  };
};

/** `GET /invoicing/:publicId` — single invoice with its lines. */
export const fetchInvoice = async (
  publicId: string
): Promise<InvoiceDetail> => {
  const res = await instance.get<InvoiceDetail>(`${BASE}/${publicId}`);

  return res.data;
};

/** `POST /invoicing` — create (and, when issue!==false, issue) an invoice. */
export const createInvoice = async (
  input: CreateInvoiceInput
): Promise<CreateInvoiceResponse> => {
  const res = await instance.post<CreateInvoiceResponse>(BASE, input);

  return res.data;
};

/**
 * `POST /invoicing/preview` — live PDF preview (nothing persisted; `issue`
 * ignored). Returns the rendered PDF as a Blob for an <iframe> via
 * URL.createObjectURL. Debounce the call on the FE.
 */
export const previewInvoicePdf = async (
  input: CreateInvoiceInput
): Promise<Blob> => {
  const res = await instance.post(`${BASE}/preview`, input, {
    responseType: 'blob'
  });

  return res.data as Blob;
};

/**
 * `POST /invoicing/preview/data` — same body as create; returns JSON with
 * computed totals + rendered fields (for drawing the preview as HTML
 * instead of the PDF). Shape is BE-defined; kept loose until needed.
 */
export const previewInvoiceData = async (
  input: CreateInvoiceInput
): Promise<unknown> => {
  const res = await instance.post(`${BASE}/preview/data`, input);

  return res.data;
};

/**
 * `GET /invoicing/:publicId/pdf` — 302-redirects to a short-lived signed
 * S3 URL; axios follows the redirect and returns the PDF Blob.
 */
export const fetchInvoicePdf = async (publicId: string): Promise<Blob> => {
  const res = await instance.get(`${BASE}/${publicId}/pdf`, {
    responseType: 'blob'
  });

  return res.data as Blob;
};

/** `GET /invoicing/export.csv` — accounting export (same filters). */
export const exportInvoicesCsv = async (
  filters: InvoiceListFilters = {}
): Promise<Blob> => {
  const res = await instance.get(`${BASE}/export.csv`, {
    params: filters,
    responseType: 'blob'
  });

  return res.data as Blob;
};

/**
 * `PUT /invoicing/:ref` — edit a DRAFT.
 *
 * THE WHOLE DOCUMENT, NOT A PATCH. Totals, VAT and the billing snapshot
 * are all derived from the lines, and line items are REPLACED rather than
 * merged — so a partial body would silently delete whatever it omitted.
 * UpdateInvoiceDto is CreateInvoiceDto minus `issue`, which is why the
 * create form's own payload builder is reused rather than a second one.
 *
 * Drafts only: an issued invoice carries a number and may already be in
 * the payer's hands, so it is corrected by reissue instead, and the 400
 * names the number.
 */
export const updateDraftInvoice = (
  ref: string,
  input: Omit<CreateInvoiceInput, 'issue'>
) => instance.put<CreateInvoiceResponse>(`${BASE}/${ref}`, input);

/**
 * `POST /invoicing/:ref/issue` — give a draft its number and render the PDF.
 *
 * The moment the document becomes real. Refused for a draft with no lines,
 * or any line with no quantity — both allowed while drafting and checked
 * here. It does NOT email anyone: /send stays the deliberate act.
 */
export const issueInvoice = (ref: string) =>
  instance.post<CreateInvoiceResponse>(`${BASE}/${ref}/issue`);

/**
 * `POST /invoicing/:ref/reissue` — correct an issued invoice.
 *
 * WHAT "EDIT" MEANS ONCE A NUMBER EXISTS. The original is cancelled and a
 * replacement issued with the corrected figures, linked back to it: a tax
 * document cannot be rewritten after the fact, because the number is
 * reported and the payer may already hold the PDF.
 *
 * DELIBERATELY NOT A CREDIT NOTE. A credit note says the charge was right
 * and part of it is coming back; this says the charge was wrong. Refused
 * on a PAID invoice — money has moved, so that is the credit note's case.
 *
 * `reason` goes on the original's audit trail: a void invoice keeps its
 * number for ever, and whoever reads the sequence will ask what happened
 * to it.
 */
export const reissueInvoice = (
  ref: string,
  input: Omit<CreateInvoiceInput, 'issue'> & { reason?: string }
) => instance.post<CreateInvoiceResponse>(`${BASE}/${ref}/reissue`, input);

/** `POST /invoicing/:publicId/cancel` — void (keeps its number); 400 if paid. */
export const cancelInvoice = (publicId: string) =>
  instance.post<Invoice>(`${BASE}/${publicId}/cancel`);

/**
 * `POST /invoicing/:publicId/mark-paid` — settle an invoice by hand.
 *
 * For a bank transfer with a receipt file use attachBankReceipt() instead:
 * it stores the proof and settles in one call. This route still accepts a
 * receiptFileId, but nothing should be passing one — there is no call that
 * mints such an id.
 */
export const markInvoicePaid = (publicId: string, input: MarkPaidInput = {}) =>
  instance.post<Invoice>(`${BASE}/${publicId}/mark-paid`, input);

/**
 * `POST /invoicing/:ref/receipt` — the bank receipt AND the settlement,
 * in one multipart call. paymentMethod is forced to bank_transfer.
 *
 * The file goes to the private store (the owner's hashed folder, a
 * storage_files row, quota counted), not the public bucket the org-logo
 * upload uses. That bucket keeps the original filename with no timestamp,
 * so two patients sending "receipt.pdf" would overwrite one another's
 * payment proof and either could be read by anyone with the URL.
 */
export const attachBankReceipt = (ref: string, input: BankReceiptInput) => {
  const body = new FormData();

  body.append('file', input.file);

  if (input.paidAt) body.append('paidAt', input.paidAt);

  if (input.paymentReference) {
    body.append('paymentReference', input.paymentReference);
  }

  return instance.post<Invoice>(`${BASE}/${ref}/receipt`, body);
};

/**
 * `GET /invoicing/:ref/receipt` — a short-lived signed URL for the stored
 * bank receipt.
 *
 * Resolved FROM THE INVOICE, never from a file id the caller supplies, so
 * it cannot be walked to read other people's private files. 404 when the
 * invoice has no receipt, which is the normal answer for a Stripe payment.
 */
/**
 * `POST /invoicing/credit-notes/:ref/void` — put the amount back on the
 * invoice.
 *
 * Only for a note that moved no money, i.e. one raised before payment:
 * once a refund has been sent there is nothing to void. The panel offers
 * it only for settlement 'reduced', which is the same rule.
 */
export const voidCreditNote = (ref: string) =>
  instance.post<Invoice>(`${BASE}/credit-notes/${ref}/void`);

export const fetchBankReceiptUrl = async (ref: string): Promise<string> => {
  const res = await instance.get<{ url?: string; data?: { url?: string } }>(
    `${BASE}/${ref}/receipt`
  );

  return res.data?.url ?? res.data?.data?.url ?? '';
};

/**
 * `POST /invoicing/:ref/send` — on-demand email the invoice to the patient.
 * Paid → paid-invoice email; issued → PDF + pay link; draft → 400.
 * `sent:false` means no email on file or the mailer failed.
 */
export const sendInvoice = async (
  ref: string
): Promise<{ sent: boolean; email: string | null }> => {
  const res = await instance.post<{ sent: boolean; email: string | null }>(
    `${BASE}/${ref}/send`
  );

  return res.data;
};

// --- Invoice-number controls (super_admin only; regular admin gets 403) ---

/** `GET /invoicing/number-counter[?prefix=]` — last/next issued number. */
export const fetchNumberCounter = async (
  prefix?: string
): Promise<NumberCounter> => {
  const res = await instance.get<NumberCounter>(`${BASE}/number-counter`, {
    params: prefix ? { prefix } : undefined
  });

  return res.data;
};

/**
 * `PUT /invoicing/number-counter` — set the NEXT invoice number (forward-only;
 * 400 below current next, 409 if ≤ the highest already used).
 */
export const setNumberCounter = async (
  nextNumber: number,
  prefix?: string
): Promise<NumberCounter> => {
  const res = await instance.put<NumberCounter>(`${BASE}/number-counter`, {
    nextNumber,
    ...(prefix ? { prefix } : {})
  });

  return res.data;
};

/** `PATCH /invoicing/:ref/number` — correct one invoice's number (409 dup). */
export const updateInvoiceNumber = (ref: string, invoiceNumber: string) =>
  instance.patch<Invoice>(`${BASE}/${ref}/number`, { invoiceNumber });
